import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Edit, XCircle, Search, Building2, Trash2, MoreVertical, Eye, Users, Filter } from 'lucide-react';
import { toast } from '../utils/toast';
import axios from 'axios';
import { sortStringOptions } from '@/utils/sortOptions';
import { getApiErrorMessage } from '@/utils/apiError';
import LeadFormModal from '@/components/LeadFormModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HospitalsPage = () => {
  // Get user role
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  // Only Super Admin can edit and deactivate hospitals
  // Exlva Admin, CRM Officer, and CRM Associate can only view
  const isSuperAdmin = user.role === 'Super Admin';
  const canEditHospitals = isSuperAdmin;
  const canViewHospitalDashboard = ['Super Admin', 'Exlva Admin', 'ExlvaAdmin'].includes(user.role);
  const isCRMUser = ['CRM Officer', 'CRM Associate'].includes(user.role);

  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState('all');
  const [staffFilter, setStaffFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [viewStatus, setViewStatus] = useState('active'); // 'active' or 'deactivated'
  const [metrics, setMetrics] = useState({});
  const [cities, setCities] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Modal states
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  // Reused Lead form (single source of truth) for editing converted hospitals
  const [leadEditOpen, setLeadEditOpen] = useState(false);
  const [leadEditId, setLeadEditId] = useState(null);
  const [leadEditSubscription, setLeadEditSubscription] = useState(null);
  const [leadEditOnboardedBy, setLeadEditOnboardedBy] = useState('');
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedHospital, setSelectedHospital] = useState(null);
  const [globalUsersOpen, setGlobalUsersOpen] = useState(false);
  const [globalUsers, setGlobalUsers] = useState([]);
  const [globalUsersLoading, setGlobalUsersLoading] = useState(false);
  const [globalUserFilters, setGlobalUserFilters] = useState({
    search: '',
    name: '',
    email: '',
    institution: '',
    user_type: 'all',
    user_category: 'all',
    user_status: 'all'
  });
  const [globalUserFilterOptions, setGlobalUserFilterOptions] = useState({ user_types: [], institutions: [], statuses: [] });
  const [selectedGlobalUser, setSelectedGlobalUser] = useState(null);
  const [globalUserDetailsOpen, setGlobalUserDetailsOpen] = useState(false);
  const [globalUserDeleteOpen, setGlobalUserDeleteOpen] = useState(false);
  const [globalUserDeleting, setGlobalUserDeleting] = useState(false);
  
  // Password reset states
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordResetLoading, setPasswordResetLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Edit form state - comprehensive hospital details (matches Lead form)
  const [editForm, setEditForm] = useState({
    // Organization details
    institution_name: '',
    institution_type: '',
    contact_email: '',
    contact_phone: '',
    // Contact Person (SPOC) details
    admin_name: '',
    spoc_mobile: '',
    spoc_email: '',
    spoc_designation: '',
    // Hospital details
    number_of_beds: '',
    number_of_staff: '',
    number_of_concurrent_staff: '',
    number_of_staff_for_quotation: '',
    gstin_uin: '',
    pan_it: '',
    accreditation_status: '',
    // Contact details
    hospital_address: '',
    city: '',
    state: '',
    country: 'India',
    pin_code: '',
    // Lead/Sales fields (from original lead data)
    lead_source: '',
    quoted_amount: '',
    important_notes: ''
  });
  
  // SPOC list for multiple contacts
  const [spocList, setSpocList] = useState([]);

  useEffect(() => {
    fetchMetrics();
    fetchCities();
  }, []);

  useEffect(() => {
    fetchHospitals();
  }, [searchTerm, subscriptionFilter, staffFilter, cityFilter, viewStatus, page]);

  useEffect(() => {
    if (globalUsersOpen) {
      fetchGlobalUsers();
    }
  }, [globalUsersOpen, globalUserFilters]);


  const fetchMetrics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/hospitals/metrics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMetrics(response.data);
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
    }
  };

  const fetchGlobalUsers = async () => {
    if (!isSuperAdmin) return;
    try {
      setGlobalUsersLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      Object.entries(globalUserFilters).forEach(([key, value]) => {
        if (value && value !== 'all') params.append(key, value);
      });
      params.append('limit', '300');
      const response = await axios.get(`${API}/super-admin/global-users?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGlobalUsers(response.data.users || []);
      setGlobalUserFilterOptions(response.data.filters || { user_types: [], institutions: [], statuses: [] });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to fetch users'));
    } finally {
      setGlobalUsersLoading(false);
    }
  };

  const handleViewGlobalUser = async (globalUser) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/super-admin/global-users/${globalUser.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedGlobalUser({ ...globalUser, details: response.data });
      setGlobalUserDetailsOpen(true);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to fetch user details'));
    }
  };

  const handleDeleteGlobalUser = (globalUser) => {
    setSelectedGlobalUser(globalUser);
    setGlobalUserDeleteOpen(true);
  };

  const confirmDeleteGlobalUser = async () => {
    if (!selectedGlobalUser) return;
    setGlobalUserDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/super-admin/global-users/${selectedGlobalUser.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted successfully');
      setGlobalUserDeleteOpen(false);
      setSelectedGlobalUser(null);
      fetchGlobalUsers();
      fetchHospitals();
      fetchMetrics();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to delete user'));
    } finally {
      setGlobalUserDeleting(false);
    }
  };

  const fetchCities = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/hospitals/cities`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCities(response.data.cities || []);
    } catch (error) {
      console.error('Failed to fetch cities:', error);
    }
  };

  const fetchHospitals = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 50);
      params.append('hospital_status', viewStatus);
      
      if (subscriptionFilter && subscriptionFilter !== 'all') {
        params.append('subscription_type', subscriptionFilter);
      }
      if (staffFilter && staffFilter !== 'all') {
        params.append('staff_count', staffFilter);
      }
      if (cityFilter && cityFilter !== 'all') {
        params.append('city', cityFilter);
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      const response = await axios.get(`${API}/hospitals/onboarded?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setHospitals(response.data.hospitals || []);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      toast.error('Failed to fetch hospitals');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewHospital = async (hospital) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/hospitals/${hospital.hospital_id}/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Preserve hospital_id at top level so Reset/Send-Welcome actions have the account id
      setSelectedHospital({ ...response.data, hospital_id: response.data?.hospital?.id || hospital.hospital_id });
      setViewModalOpen(true);
      // Reset password form
      setShowPasswordReset(false);
      setShowPassword(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error('Failed to fetch hospital details');
    }
  };

  const handleEditHospital = async (hospital) => {
    // Fetch full details (subscription + lead-overlaid institution fields) so the
    // edit form and Subscription tab load the shared source-of-truth data.
    let merged = hospital;
    let subscription = null;
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(`${API}/hospitals/${hospital.hospital_id}/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      subscription = resp.data?.subscription || null;
      // details.hospital carries the lead-overlaid common fields
      merged = { ...hospital, ...(resp.data?.hospital || {}) };
    } catch (e) {
      console.error('Failed to load hospital details for edit:', e);
    }

    // Preferred: converted hospitals are backed by a Lead (single source of truth).
    // Open the EXACT same Lead form (with a Subscription tab) instead of a separate form.
    const linkedLeadId = merged.linked_lead_id || hospital.linked_lead_id;
    if (linkedLeadId) {
      setLeadEditId(linkedLeadId);
      setLeadEditSubscription(subscription);
      setLeadEditOnboardedBy(merged.onboarded_by_name || hospital.onboarded_by_name || '');
      setLeadEditOpen(true);
      return;
    }

    // Fallback (legacy hospitals with no linked lead): the standalone edit modal
    setSelectedHospital({ ...merged, hospital_id: hospital.hospital_id, subscription });
    hospital = merged;
    setEditForm({
      // Organization details - ensure strings
      institution_name: String(hospital.hospital_name || hospital.institution_name || ''),
      institution_type: String(hospital.institution_type || ''),
      contact_email: String(hospital.email || hospital.contact_email || ''),
      contact_phone: String(hospital.phone_number || hospital.contact_phone || ''),
      // Contact Person (SPOC) details
      admin_name: String(hospital.full_name || hospital.admin_name || ''),
      spoc_mobile: String(hospital.phone_number || hospital.spoc_mobile || ''),
      spoc_email: String(hospital.email || hospital.spoc_email || ''),
      spoc_designation: String(hospital.designation || hospital.spoc_designation || ''),
      // Hospital details
      number_of_beds: String(hospital.number_of_beds || ''),
      number_of_staff: String(hospital.number_of_staff || ''),
      number_of_concurrent_staff: String(hospital.number_of_concurrent_staff || ''),
      number_of_staff_for_quotation: String(hospital.number_of_staff_for_quotation || ''),
      gstin_uin: String(hospital.gstin_uin || ''),
      pan_it: String(hospital.pan_it || ''),
      accreditation_status: String(hospital.accreditation_status || ''),
      // Contact details
      hospital_address: String(hospital.hospital_address || ''),
      city: String(hospital.city || ''),
      state: String(hospital.state || ''),
      country: String(hospital.country || 'India'),
      pin_code: String(hospital.pin_code || ''),
      // Lead/Sales fields
      lead_source: String(hospital.lead_source || ''),
      quoted_amount: String(hospital.quoted_amount || ''),
      important_notes: String(hospital.important_notes || '')
    });
    // Set SPOC list if available
    if (hospital.spoc_details && hospital.spoc_details.length > 0) {
      setSpocList(hospital.spoc_details);
    } else {
      setSpocList([]);
    }
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Prepare payload with proper type conversions
      const payload = {
        ...editForm,
        // Convert numeric fields to integers, send null if empty/non-numeric (won't overwrite)
        number_of_beds: Number.isInteger(parseInt(editForm.number_of_beds)) && /^\d+$/.test(String(editForm.number_of_beds).trim()) ? parseInt(editForm.number_of_beds) : null,
        number_of_staff: Number.isInteger(parseInt(editForm.number_of_staff)) && /^\d+$/.test(String(editForm.number_of_staff).trim()) ? parseInt(editForm.number_of_staff) : null,
        number_of_concurrent_staff: editForm.number_of_concurrent_staff ? parseInt(editForm.number_of_concurrent_staff) : null,
        number_of_staff_for_quotation: editForm.number_of_staff_for_quotation ? parseInt(editForm.number_of_staff_for_quotation) : null,
        quoted_amount: editForm.quoted_amount ? parseFloat(editForm.quoted_amount) : null,
        // Send the full SPOC list so contact details sync to the shared lead record
        spoc_details: (spocList || []).filter((s) => [s.spoc_name, s.spoc_email, s.spoc_mobile, s.spoc_designation].some((v) => (v || '').trim())),
      };
      if (!payload.spoc_details.length) delete payload.spoc_details;
      
      console.log('Saving hospital edit with data:', payload);
      console.log('Hospital ID:', selectedHospital.hospital_id);
      
      const response = await axios.put(`${API}/hospitals/${selectedHospital.hospital_id}/edit`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Update response:', response.data);
      toast.success('Hospital details updated successfully');
      setEditModalOpen(false);
      fetchHospitals();
      fetchMetrics();
    } catch (error) {
      console.error('Failed to update hospital:', error.response?.data || error);
      toast.error(error.response?.data?.detail || 'Failed to update hospital');
    }
  };

  const handleDeactivateHospital = (hospital) => {
    setSelectedHospital(hospital);
    setDeactivateModalOpen(true);
  };

  const confirmDeactivate = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/hospitals/${selectedHospital.hospital_id}/deactivate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const action = selectedHospital.is_active ? 'deactivated' : 'activated';
      toast.success(`Hospital ${action} successfully`);
      setDeactivateModalOpen(false);
      fetchHospitals();
      fetchMetrics();
    } catch (error) {
      toast.error('Failed to update hospital status');
    }
  };

  const handleDeleteHospital = (hospital) => {
    setSelectedHospital(hospital);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async (deleteRelatedLead = false) => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/hospitals/${selectedHospital.hospital_id}`, {
        params: { delete_related_lead: deleteRelatedLead },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(
        deleteRelatedLead
          ? 'Institution and related lead deleted successfully. Email ID is now available for reuse.'
          : 'Institution deleted successfully. Related lead was retained.'
      );
      setDeleteModalOpen(false);
      fetchHospitals();
      fetchMetrics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete institution');
    } finally {
      setDeleting(false);
    }
  };

  const handleViewHospitalDashboard = async (hospital) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/hospitals/${hospital.hospital_id}/impersonate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { token: impToken, user: impUser, impersonated_by } = response.data;
      const encodedUser = btoa(JSON.stringify(impUser));
      const url = `/view-hospital?t=${encodeURIComponent(impToken)}&u=${encodeURIComponent(encodedUser)}&by=${encodeURIComponent(impersonated_by)}`;
      window.open(url, '_blank');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open hospital dashboard');
    }
  };

  // Reset generates a NEW secure temporary password (server-side), forces change on next
  // login, and returns it once so the Super Admin can share it.
  const handleResetPassword = async () => {
    setPasswordResetLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API}/hospitals/${selectedHospital.hospital_id}/reset-password`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('A new temporary password has been generated.');
      setSelectedHospital({
        ...selectedHospital,
        admin_credentials: {
          ...(selectedHospital.admin_credentials || {}),
          password: response.data.new_password,
          password_changed: false,
          has_temp_password: true
        }
      });
      setShowPassword(true);
    } catch (error) {
      console.error('Password reset error:', error);
      toast.error(getApiErrorMessage(error, 'Failed to reset password'));
    } finally {
      setPasswordResetLoading(false);
    }
  };

  // Change / add Hospital Admin (single-active + audit trail)
  const [changeAdminOpen, setChangeAdminOpen] = useState(false);
  const [changeAdminForm, setChangeAdminForm] = useState({ full_name: '', email: '', mobile: '' });
  const [changingAdmin, setChangingAdmin] = useState(false);
  const handleChangeAdmin = async () => {
    if (!changeAdminForm.full_name.trim()) { toast.error('Full name is required'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(changeAdminForm.email.trim())) { toast.error('A valid email is required'); return; }
    setChangingAdmin(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(
        `${API}/hospitals/${selectedHospital.hospital_id}/change-admin`,
        { full_name: changeAdminForm.full_name.trim(), email: changeAdminForm.email.trim(), mobile: changeAdminForm.mobile.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Hospital Admin updated. A new temporary password was generated.');
      const prev = selectedHospital.hospital || {};
      setSelectedHospital({
        ...selectedHospital,
        hospital: {
          ...prev,
          full_name: changeAdminForm.full_name.trim(),
          email: resp.data.email,
          phone_number: changeAdminForm.mobile.trim() || prev.phone_number,
          welcome_email_sent: false,
          welcome_email_sent_at: null,
          hospital_admin_history: [
            ...(prev.hospital_admin_history || []),
            { full_name: prev.full_name, email: prev.email, mobile: prev.phone_number, replaced_at: new Date().toISOString() }
          ]
        },
        admin_credentials: {
          ...(selectedHospital.admin_credentials || {}),
          username: resp.data.email, email: resp.data.email,
          password: resp.data.new_password, password_changed: false, has_temp_password: true
        }
      });
      setShowPassword(true);
      setChangeAdminOpen(false);
      setChangeAdminForm({ full_name: '', email: '', mobile: '' });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to change Hospital Admin'));
    } finally {
      setChangingAdmin(false);
    }
  };

  const [sendingWelcome, setSendingWelcome] = useState(false);
  const handleSendWelcomeEmail = async () => {
    setSendingWelcome(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(
        `${API}/hospitals/${selectedHospital.hospital_id}/send-welcome-email`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Welcome email sent successfully');
      setSelectedHospital({
        ...selectedHospital,
        hospital: {
          ...(selectedHospital.hospital || {}),
          welcome_email_sent: true,
          welcome_email_sent_at: resp.data.welcome_email_sent_at
        }
      });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to send welcome email'));
    } finally {
      setSendingWelcome(false);
    }
  };

  const copyToClipboard = async (text, label = 'Copied') => {
    try {
      await navigator.clipboard.writeText(text || '');
      toast.success(`${label} to clipboard`);
    } catch (e) {
      toast.error('Failed to copy');
    }
  };

  const formatAddedOn = (value) => {
    if (!value) return '-';
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return '-';
    return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };


  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading hospitals...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Page Title */}
        <div className="flex items-center justify-between gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-900">ONBOARDED HOSPITALS</h1>
          {isSuperAdmin && (
            <Button
              variant="outline"
              size="icon"
              className="h-10 w-10"
              onClick={() => setGlobalUsersOpen(true)}
              title="Global User Management"
            >
              <Users className="w-5 h-5" />
            </Button>
          )}
        </div>

        {/* Subscription Stats Cards - Matching Dashboard Style */}
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
               onClick={() => { setViewStatus('active'); setSubscriptionFilter('Core Plan'); }}>
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-purple-700 mb-2">Core Plan</p>
                <h3 className="text-4xl font-bold text-purple-900">{metrics.core_plan || 0}</h3>
              </div>
              <div className="w-12 h-12 bg-purple-200 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-purple-700" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
               onClick={() => { setViewStatus('active'); setSubscriptionFilter('Advanced Plan'); }}>
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-pink-700 mb-2">Advanced Plan</p>
                <h3 className="text-4xl font-bold text-pink-900">{metrics.advanced_plan || 0}</h3>
              </div>
              <div className="w-12 h-12 bg-pink-200 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-pink-700" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
               onClick={() => { setViewStatus('active'); setSubscriptionFilter('Enterprise Plan'); }}>
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-yellow-700 mb-2">Enterprise</p>
                <h3 className="text-4xl font-bold text-yellow-900">{metrics.enterprise_plan || 0}</h3>
              </div>
              <div className="w-12 h-12 bg-yellow-200 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-yellow-700" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
               onClick={() => { setViewStatus('deactivated'); setSubscriptionFilter('all'); }}>
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-red-700 mb-2">Deactivated</p>
                <h3 className="text-4xl font-bold text-red-900">{metrics.deactivated || 0}</h3>
              </div>
              <div className="w-12 h-12 bg-red-200 rounded-lg flex items-center justify-center">
                <XCircle className="w-4 h-4 text-red-700" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
               onClick={() => { setViewStatus('active'); setSubscriptionFilter('all'); }}>
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-blue-700 mb-2">Onboarded</p>
                <h3 className="text-4xl font-bold text-blue-900">{(metrics.total_active || 0) + (metrics.deactivated || 0)}</h3>
              </div>
              <div className="w-12 h-12 bg-blue-200 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-blue-700" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
               onClick={() => { setViewStatus('active'); setSubscriptionFilter('No Plan'); }}>
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">Unassigned Plan</p>
                <h3 className="text-4xl font-bold text-slate-900">{metrics.no_plan || 0}</h3>
              </div>
              <div className="w-12 h-12 bg-slate-200 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-slate-700" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search - Matching CRM Style */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search hospitals..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={subscriptionFilter} onValueChange={setSubscriptionFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by Subscription" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Subscriptions</SelectItem>
                <SelectItem value="Core Plan">Core Plan</SelectItem>
                <SelectItem value="Advanced Plan">Advanced Plan</SelectItem>
                <SelectItem value="Enterprise Plan">Enterprise Plan</SelectItem>
                <SelectItem value="No Plan">No Plan</SelectItem>
              </SelectContent>
            </Select>

            <Select value={staffFilter} onValueChange={setStaffFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by Staff" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Staff Levels</SelectItem>
                <SelectItem value="Up to 50">Up to 50</SelectItem>
                <SelectItem value="Up to 100">Up to 100</SelectItem>
                <SelectItem value="Up to 200">Up to 200</SelectItem>
              </SelectContent>
            </Select>

            <Select value={cityFilter} onValueChange={setCityFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by City" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {sortStringOptions(cities).map(city => (
                  <SelectItem key={city} value={city}>{city}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {viewStatus === 'deactivated' && (
            <div className="mt-4 flex justify-end">
              <Button 
                variant="outline" 
                onClick={() => setViewStatus('active')}
                className="border-[#0056B3] text-[#0056B3]"
              >
                View Active Hospitals
              </Button>
            </div>
          )}
        </div>

        {/* Hospitals Table - Matching CRM Style */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Institution Name</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead className="text-center">Staff Added</TableHead>
                  <TableHead>Staff Capacity</TableHead>
                  <TableHead>Subscription Type</TableHead>
                  <TableHead className="text-right">Invoice Amount</TableHead>
                  <TableHead className="w-[110px] text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {hospitals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      No hospitals found
                    </TableCell>
                  </TableRow>
                ) : (
                  hospitals.map((hospital) => (
                    <TableRow
                      key={hospital.hospital_id}
                      className="cursor-pointer"
                      onClick={() => handleViewHospital(hospital)}
                    >
                      <TableCell>
                        <div className="leading-tight">
                          <div className="font-medium text-gray-900 truncate">{hospital.hospital_name}</div>
                          <div className="text-[11px] italic text-gray-500 truncate">
                            Onboarded by: {hospital.onboarded_by_name || hospital.created_by_name || 'System'}
                          </div>
                          <div className="text-[11px] italic text-gray-500 truncate">
                            Added on: {formatAddedOn(hospital.created_at)}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{hospital.city}</TableCell>
                      <TableCell className="text-center">{hospital.staff_added}</TableCell>
                      <TableCell>{hospital.staff_capacity}</TableCell>
                      <TableCell>
                        <Badge className={
                          hospital.subscription_type === 'Core Plan' ? 'bg-purple-100 text-purple-800' :
                          hospital.subscription_type === 'Advanced Plan' ? 'bg-pink-100 text-pink-800' :
                          hospital.subscription_type === 'Enterprise Plan' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }>
                          {hospital.subscription_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {hospital.invoice_amount ? `₹${hospital.invoice_amount.toLocaleString('en-IN')}` : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end space-x-1" onClick={(e) => e.stopPropagation()}>
                          {canEditHospitals && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditHospital(hospital)}
                              data-tooltip="Edit Hospital"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}
                          {(canEditHospitals || canViewHospitalDashboard) && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" data-tooltip="More Actions">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {canViewHospitalDashboard && (
                                  <DropdownMenuItem
                                    onClick={() => handleViewHospitalDashboard(hospital)}
                                    className="text-blue-600"
                                  >
                                    <Eye className="w-4 h-4 mr-2 text-blue-600" />
                                    View Hospital Dashboard
                                  </DropdownMenuItem>
                                )}
                                {canEditHospitals && (
                                  <DropdownMenuItem
                                    onClick={() => handleDeactivateHospital(hospital)}
                                    className={hospital.is_active ? 'text-red-600' : 'text-green-700'}
                                  >
                                    <XCircle className={`w-4 h-4 mr-2 ${hospital.is_active ? 'text-red-600' : 'text-green-700'}`} />
                                    {hospital.is_active ? 'Deactivate' : 'Activate'}
                                  </DropdownMenuItem>
                                )}
                                {canEditHospitals && hospital.can_delete && (
                                  <DropdownMenuItem
                                    onClick={() => handleDeleteHospital(hospital)}
                                    className="text-red-600"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2 text-red-600" />
                                    Delete
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {hospitals.length > 0 && (
            <div className="flex justify-between items-center mt-6 pt-4 border-t">
              <p className="text-sm text-gray-600">
                Showing page {page} of {totalPages}
              </p>
              <div className="flex space-x-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </Button>
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  const pageNum = i + 1;
                  return (
                    <Button 
                      key={pageNum}
                      size="sm" 
                      className={page === pageNum ? "bg-[#0056B3] hover:bg-[#0056B3]/90" : ""}
                      variant={page === pageNum ? "default" : "outline"}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
                <Button 
                  variant="outline" 
                  size="sm"
                  disabled={page === totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* View Hospital Modal */}
        <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
          <DialogContent className="max-w-3xl max-h-[85vh] p-0 gap-0 flex flex-col overflow-hidden">
            <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
              <DialogTitle className="text-[20px] font-bold text-[#0056B3]">Hospital Details</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4">
            {selectedHospital && (() => {
              const h = selectedHospital.hospital || {};
              const cred = selectedHospital.admin_credentials || null;
              const sub = selectedHospital.subscription || null;
              const Field = ({ label, value }) => (
                <div className="rounded-lg border bg-gray-50 px-3 py-2">
                  <p className="text-xs text-gray-500">{label}</p>
                  <p className="text-sm font-medium text-gray-900 break-words">{value || '—'}</p>
                </div>
              );
              return (
              <div className="space-y-6">
                {/* Section 1 — Hospital Information */}
                <section>
                  <h3 className="text-base font-semibold text-gray-900 mb-3">Hospital Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Institution Name" value={h.institution_name} />
                    <Field label="Type of Institution" value={h.institution_type} />
                    <Field label="City" value={h.city} />
                    <Field label="State" value={h.state} />
                    <Field label="Hospital Admin Email" value={h.email} />
                    <Field label="Staff Count" value={selectedHospital.staff_count ?? 0} />
                    <Field label="Department Count" value={selectedHospital.department_count ?? 0} />
                    <Field label="Onboarded By" value={h.onboarded_by_name} />
                    <Field label="Onboarded Date" value={formatAddedOn(h.created_at)} />
                  </div>
                </section>

                {/* Section 2 — Login Credentials (Super Admin only) */}
                {cred && (
                  <section>
                    <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                      <h3 className="text-base font-semibold text-gray-900">Login Credentials</h3>
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={handleSendWelcomeEmail}
                          disabled={sendingWelcome}
                          size="sm"
                          className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white"
                        >
                          {sendingWelcome ? 'Sending…' : (h.welcome_email_sent ? 'Resend Welcome Email' : 'Send Welcome Email')}
                        </Button>
                        <Button
                          onClick={handleResetPassword}
                          disabled={passwordResetLoading}
                          variant="outline"
                          size="sm"
                          className="text-[#0056B3] border-[#0056B3]/40 hover:bg-blue-50"
                        >
                          {passwordResetLoading ? 'Generating…' : 'Reset Password'}
                        </Button>
                      </div>
                    </div>
                    {h.welcome_email_sent && (
                      <p className="text-xs text-green-700 mb-2">
                        ✓ Welcome Email Sent{h.welcome_email_sent_at ? ` on ${formatAddedOn(h.welcome_email_sent_at)}` : ''}
                      </p>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="rounded-lg border bg-gray-50 px-3 py-2">
                        <p className="text-xs text-gray-500">Username / Email</p>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-mono font-medium text-gray-900 truncate">{cred.username || cred.email}</p>
                          <button type="button" onClick={() => copyToClipboard(cred.username || cred.email, 'Username copied')} className="text-xs text-[#0056B3] hover:underline shrink-0">Copy</button>
                        </div>
                      </div>
                      <div className="rounded-lg border bg-gray-50 px-3 py-2">
                        <p className="text-xs text-gray-500">Temporary Password</p>
                        {cred.has_temp_password && cred.password ? (
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-mono font-medium text-gray-900 truncate">
                              {showPassword ? cred.password : '••••••••••'}
                            </p>
                            <button type="button" onClick={() => setShowPassword((s) => !s)} className="text-xs text-gray-600 hover:underline shrink-0">{showPassword ? 'Hide' : 'Show'}</button>
                            <button type="button" onClick={() => copyToClipboard(cred.password, 'Password copied')} className="text-xs text-[#0056B3] hover:underline shrink-0">Copy</button>
                          </div>
                        ) : cred.password_changed ? (
                          <p className="text-sm text-gray-600 italic">Password has been changed by the user.</p>
                        ) : (
                          <p className="text-sm text-gray-500 italic">No temporary password on record. Use Reset Password to generate one.</p>
                        )}
                      </div>
                    </div>
                    <p className="text-[11px] text-gray-400 mt-2">
                      The temporary password is visible only until the user changes it on first login. Resetting forces a change on next login.
                    </p>
                  </section>
                )}

                {/* Section — Hospital Admin Users (single active + audit trail) */}
                {cred && (
                  <section>
                    <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                      <h3 className="text-base font-semibold text-gray-900">Hospital Admin Users</h3>
                      <Button
                        onClick={() => { setChangeAdminForm({ full_name: '', email: '', mobile: '' }); setChangeAdminOpen(true); }}
                        size="sm"
                        variant="outline"
                        className="text-[#0056B3] border-[#0056B3]/40 hover:bg-blue-50"
                      >
                        Change Hospital Admin
                      </Button>
                    </div>
                    <div className="rounded-lg border overflow-hidden">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-green-50/40">
                        <Field label="Full Name" value={h.full_name} />
                        <div className="rounded-lg border bg-white px-3 py-2">
                          <p className="text-xs text-gray-500">Status</p>
                          <Badge className="bg-green-100 text-green-800 mt-0.5">{h.is_active === false ? 'Inactive' : 'Active'}</Badge>
                        </div>
                        <Field label="Email (Username)" value={h.email} />
                        <Field label="Mobile" value={h.phone_number} />
                        <Field label="Last Password Reset" value={h.last_password_reset_at ? formatAddedOn(h.last_password_reset_at) : '—'} />
                        <Field label="Last Login" value={h.last_login ? formatAddedOn(h.last_login) : 'Never'} />
                      </div>
                    </div>
                    {Array.isArray(h.hospital_admin_history) && h.hospital_admin_history.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-gray-500 mb-1">Previous Administrators</p>
                        <div className="space-y-1">
                          {h.hospital_admin_history.slice().reverse().map((p, i) => (
                            <div key={i} className="text-xs text-gray-600 border rounded px-3 py-1.5 flex justify-between gap-2">
                              <span className="truncate">{p.full_name || '—'} · {p.email}</span>
                              <span className="text-gray-400 shrink-0">{p.replaced_at ? formatAddedOn(p.replaced_at) : ''}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <p className="text-[11px] text-gray-400 mt-2">
                      Only one Hospital Admin is active at a time. Changing the admin repoints the login to the new contact and generates a new temporary password; the previous admin is kept here for audit.
                    </p>
                  </section>
                )}

                {/* Section 3 — Subscription Details */}
                <section>
                  <h3 className="text-base font-semibold text-gray-900 mb-3">Subscription Details</h3>
                  {sub ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Field label="Current Plan" value={sub.plan_name} />
                      <div className="rounded-lg border bg-gray-50 px-3 py-2">
                        <p className="text-xs text-gray-500">Status</p>
                        <Badge className="bg-green-100 text-green-800 capitalize mt-0.5">{sub.status || '—'}</Badge>
                      </div>
                      <Field label="Total Licenses" value={sub.total_licenses ?? sub.license_count} />
                      <Field label="Concurrent Users" value={sub.concurrent_users} />
                      <Field label="Start Date" value={sub.start_date ? formatAddedOn(sub.start_date) : '—'} />
                      <Field label="Expiry Date" value={sub.end_date ? formatAddedOn(sub.end_date) : '—'} />
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No active subscription.</p>
                  )}
                </section>
              </div>
              );
            })()}
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Hospital Modal - Comprehensive View */}
        <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader className="shrink-0 border-b pb-3">
              <DialogTitle className="text-[22px] font-bold text-[#0056B3]">Edit Hospital Details</DialogTitle>
              {selectedHospital?.onboarded_by_name ? (
                <p className="text-sm text-gray-600">
                  Onboarded By: <span className="font-medium text-gray-800">{selectedHospital.onboarded_by_name}</span>
                </p>
              ) : (
                <DialogDescription>
                  Update hospital information across all sections
                </DialogDescription>
              )}
            </DialogHeader>

            <div className="flex-1 overflow-y-auto min-h-0 mt-4">

            <div className="space-y-6">
              {/* Subscription (hospital-specific) */}
              <div className="border rounded-lg p-4 bg-blue-50/40">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <Building2 className="w-5 h-5 mr-2 text-[#0056B3]" />
                  Subscription
                </h3>
                {selectedHospital?.subscription ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    <div><p className="text-gray-500">Current Plan</p><p className="font-medium">{selectedHospital.subscription.plan_name || '—'}</p></div>
                    <div><p className="text-gray-500">Status</p><p className="font-medium capitalize">{selectedHospital.subscription.status || '—'}</p></div>
                    <div><p className="text-gray-500">User Limit</p><p className="font-medium">{selectedHospital.subscription.total_licenses ?? selectedHospital.subscription.license_count ?? '—'}</p></div>
                    <div><p className="text-gray-500">Start Date</p><p className="font-medium">{selectedHospital.subscription.start_date ? new Date(selectedHospital.subscription.start_date).toLocaleDateString() : '—'}</p></div>
                    <div><p className="text-gray-500">End Date</p><p className="font-medium">{selectedHospital.subscription.end_date ? new Date(selectedHospital.subscription.end_date).toLocaleDateString() : '—'}</p></div>
                    <div><p className="text-gray-500">Concurrent Users</p><p className="font-medium">{selectedHospital.subscription.concurrent_users ?? '—'}</p></div>
                    {selectedHospital.subscription.grace_period_days != null && (
                      <div><p className="text-gray-500">Grace Period</p><p className="font-medium">{selectedHospital.subscription.grace_period_days} days</p></div>
                    )}
                    {selectedHospital.subscription.discount != null && (
                      <div><p className="text-gray-500">Discount</p><p className="font-medium">{selectedHospital.subscription.discount}</p></div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No active subscription for this hospital.</p>
                )}
                <p className="text-[11px] text-gray-400 mt-2">Subscription is hospital-specific. Manage plans and validity from the Subscriptions module.</p>
              </div>

              {/* Organization Details Subchapter */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Building2 className="w-5 h-5 mr-2 text-[#0056B3]" />
                  Organization Details
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Organization Name *</label>
                    <Input
                      type="text"
                      value={String(editForm.institution_name || '')}
                      onChange={(e) => setEditForm({...editForm, institution_name: e.target.value})}
                      className="focus:ring-2 focus:ring-[#0056B3]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Type of Organization</label>
                    <Select
                      value={editForm.institution_type || ''}
                      onValueChange={(value) => setEditForm({...editForm, institution_type: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Hospital">Hospital</SelectItem>
                        <SelectItem value="Clinic">Clinic</SelectItem>
                        <SelectItem value="Nursing Home">Nursing Home</SelectItem>
                        <SelectItem value="Medical College">Medical College</SelectItem>
                        <SelectItem value="Diagnostic Center">Diagnostic Center</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Contact Email Address *</label>
                    <Input
                      type="email"
                      value={editForm.contact_email}
                      onChange={(e) => setEditForm({...editForm, contact_email: e.target.value})}
                      className="focus:ring-2 focus:ring-[#0056B3]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Contact Phone Number *</label>
                    <Input
                      type="tel"
                      value={editForm.contact_phone}
                      onChange={(e) => setEditForm({...editForm, contact_phone: e.target.value})}
                      className="focus:ring-2 focus:ring-[#0056B3]"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Person (SPOC) Details Subchapter */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Person (SPOC) Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Admin/SPOC Name</label>
                    <Input
                      type="text"
                      value={editForm.admin_name}
                      onChange={(e) => setEditForm({...editForm, admin_name: e.target.value})}
                      className="focus:ring-2 focus:ring-[#0056B3]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">SPOC Mobile Number</label>
                    <Input
                      type="tel"
                      value={editForm.spoc_mobile}
                      onChange={(e) => setEditForm({...editForm, spoc_mobile: e.target.value})}
                      className="focus:ring-2 focus:ring-[#0056B3]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">SPOC Email Address</label>
                    <Input
                      type="email"
                      value={editForm.spoc_email}
                      onChange={(e) => setEditForm({...editForm, spoc_email: e.target.value})}
                      className="focus:ring-2 focus:ring-[#0056B3]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Designation</label>
                    <Input
                      type="text"
                      value={editForm.spoc_designation}
                      onChange={(e) => setEditForm({...editForm, spoc_designation: e.target.value})}
                      className="focus:ring-2 focus:ring-[#0056B3]"
                    />
                  </div>
                </div>
              </div>

              {/* Hospital Details Subchapter */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Hospital Details</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Number of Beds</label>
                    <Input
                      type="number"
                      value={editForm.number_of_beds}
                      onChange={(e) => setEditForm({...editForm, number_of_beds: e.target.value})}
                      className="focus:ring-2 focus:ring-[#0056B3]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Number of Staff</label>
                    <Input
                      type="number"
                      value={editForm.number_of_staff}
                      onChange={(e) => setEditForm({...editForm, number_of_staff: e.target.value})}
                      className="focus:ring-2 focus:ring-[#0056B3]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Concurrent Staff</label>
                    <Input
                      type="number"
                      value={editForm.number_of_concurrent_staff}
                      onChange={(e) => setEditForm({...editForm, number_of_concurrent_staff: e.target.value})}
                      className="focus:ring-2 focus:ring-[#0056B3]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">GSTIN/UIN Number</label>
                    <Input
                      type="text"
                      value={editForm.gstin_uin}
                      onChange={(e) => setEditForm({...editForm, gstin_uin: e.target.value})}
                      className="focus:ring-2 focus:ring-[#0056B3]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">PAN/IT Number</label>
                    <Input
                      type="text"
                      value={editForm.pan_it}
                      onChange={(e) => setEditForm({...editForm, pan_it: e.target.value})}
                      className="focus:ring-2 focus:ring-[#0056B3]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Accreditation Status</label>
                    <Select
                      value={editForm.accreditation_status || ''}
                      onValueChange={(value) => setEditForm({...editForm, accreditation_status: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NABH">NABH</SelectItem>
                        <SelectItem value="NABL">NABL</SelectItem>
                        <SelectItem value="JCI">JCI</SelectItem>
                        <SelectItem value="ISO">ISO</SelectItem>
                        <SelectItem value="None">None</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Contact Details Subchapter */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">Hospital Address</label>
                    <Input
                      type="text"
                      value={editForm.hospital_address}
                      onChange={(e) => setEditForm({...editForm, hospital_address: e.target.value})}
                      className="focus:ring-2 focus:ring-[#0056B3]"
                      placeholder="Street address, building, floor"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">City *</label>
                    <Input
                      type="text"
                      value={editForm.city}
                      onChange={(e) => setEditForm({...editForm, city: e.target.value})}
                      className="focus:ring-2 focus:ring-[#0056B3]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">State</label>
                    <Input
                      type="text"
                      value={editForm.state}
                      onChange={(e) => setEditForm({...editForm, state: e.target.value})}
                      className="focus:ring-2 focus:ring-[#0056B3]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Country</label>
                    <Input
                      type="text"
                      value={editForm.country}
                      onChange={(e) => setEditForm({...editForm, country: e.target.value})}
                      className="focus:ring-2 focus:ring-[#0056B3]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Pincode</label>
                    <Input
                      type="text"
                      value={editForm.pin_code}
                      onChange={(e) => setEditForm({...editForm, pin_code: e.target.value})}
                      className="focus:ring-2 focus:ring-[#0056B3]"
                    />
                  </div>
                </div>
              </div>
              
              {/* Additional Information Subchapter */}
              <div className="pb-2">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Lead Source</label>
                    <Select
                      value={editForm.lead_source || ''}
                      onValueChange={(value) => setEditForm({...editForm, lead_source: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select source" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Website">Website</SelectItem>
                        <SelectItem value="Referral">Referral</SelectItem>
                        <SelectItem value="Cold Call">Cold Call</SelectItem>
                        <SelectItem value="Email Campaign">Email Campaign</SelectItem>
                        <SelectItem value="Trade Show">Trade Show</SelectItem>
                        <SelectItem value="Social Media">Social Media</SelectItem>
                        <SelectItem value="Partner">Partner</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Staff for Quotation</label>
                    <Input
                      type="number"
                      value={editForm.number_of_staff_for_quotation}
                      onChange={(e) => setEditForm({...editForm, number_of_staff_for_quotation: e.target.value})}
                      className="focus:ring-2 focus:ring-[#0056B3]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Quoted Amount (₹)</label>
                    <Input
                      type="number"
                      value={editForm.quoted_amount}
                      onChange={(e) => setEditForm({...editForm, quoted_amount: e.target.value})}
                      className="focus:ring-2 focus:ring-[#0056B3]"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">Important Notes</label>
                    <textarea
                      value={editForm.important_notes}
                      onChange={(e) => setEditForm({...editForm, important_notes: e.target.value})}
                      className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-[#0056B3] focus:outline-none min-h-[80px]"
                      placeholder="Any additional notes"
                    />
                  </div>
                </div>
              </div>
            </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModalOpen(false)}>Cancel</Button>
              <Button className="bg-[#0056B3] hover:bg-[#0056B3]/90" onClick={handleSaveEdit}>
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit converted hospital via the EXACT Lead form (single source of truth) + Subscription tab */}
        <LeadFormModal
          isOpen={leadEditOpen}
          onClose={() => { setLeadEditOpen(false); setLeadEditId(null); setLeadEditSubscription(null); }}
          leadId={leadEditId}
          hospitalMode={true}
          fullEditConverted={isSuperAdmin}
          viewOnly={!isSuperAdmin}
          subscriptionInfo={leadEditSubscription}
          canManageSubscription={isSuperAdmin}
          titleOverride="Edit Hospital"
          onboardedByOverride={leadEditOnboardedBy}
          onSuccess={() => { fetchHospitals(); fetchMetrics(); }}
        />

        {/* Change / Add Hospital Admin */}
        <Dialog open={changeAdminOpen} onOpenChange={(open) => { if (!open) setChangeAdminOpen(false); }}>
          <DialogContent className="sm:max-w-[460px]">
            <DialogHeader>
              <DialogTitle>Change Hospital Admin</DialogTitle>
              <DialogDescription>
                Assign a new administrator for this institution. The login is repointed to this person, a new
                temporary password is generated, and the previous admin is archived for audit.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Full Name *</label>
                <Input value={changeAdminForm.full_name} onChange={(e) => setChangeAdminForm({ ...changeAdminForm, full_name: e.target.value })} placeholder="Enter full name" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email (Username) *</label>
                <Input type="email" value={changeAdminForm.email} onChange={(e) => setChangeAdminForm({ ...changeAdminForm, email: e.target.value })} placeholder="Enter email address" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Mobile Number</label>
                <Input value={changeAdminForm.mobile} onChange={(e) => setChangeAdminForm({ ...changeAdminForm, mobile: e.target.value })} placeholder="Optional" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setChangeAdminOpen(false)} disabled={changingAdmin}>Cancel</Button>
              <Button className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white" onClick={handleChangeAdmin} disabled={changingAdmin}>
                {changingAdmin ? 'Saving…' : 'Assign Admin'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Global User Management */}
        <Dialog open={globalUsersOpen} onOpenChange={setGlobalUsersOpen}>
          <DialogContent className="w-[96vw] sm:max-w-[96vw] max-h-[92vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[#0056B3]">
                <Users className="w-5 h-5" />
                Global User Management
              </DialogTitle>
              <DialogDescription>
                View and manage every application user across internal teams and hospitals.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 overflow-hidden flex-1 flex flex-col">
              <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3">
                <div className="relative md:col-span-2">
                  <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search users, email, hospital..."
                    value={globalUserFilters.search}
                    onChange={(e) => setGlobalUserFilters({ ...globalUserFilters, search: e.target.value })}
                    className="pl-10"
                  />
                </div>
                <Input
                  placeholder="User name"
                  value={globalUserFilters.name}
                  onChange={(e) => setGlobalUserFilters({ ...globalUserFilters, name: e.target.value })}
                />
                <Input
                  placeholder="Email ID"
                  value={globalUserFilters.email}
                  onChange={(e) => setGlobalUserFilters({ ...globalUserFilters, email: e.target.value })}
                />
                <Input
                  placeholder="Hospital / Institution"
                  value={globalUserFilters.institution}
                  onChange={(e) => setGlobalUserFilters({ ...globalUserFilters, institution: e.target.value })}
                />
                <Button
                  variant="outline"
                  onClick={() => setGlobalUserFilters({ search: '', name: '', email: '', institution: '', user_type: 'all', user_category: 'all', user_status: 'all' })}
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Reset
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Select value={globalUserFilters.user_type} onValueChange={(value) => setGlobalUserFilters({ ...globalUserFilters, user_type: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="User Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All User Types</SelectItem>
                    {(globalUserFilterOptions.user_types || []).map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={globalUserFilters.user_category} onValueChange={(value) => setGlobalUserFilters({ ...globalUserFilters, user_category: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="User Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="internal">Internal</SelectItem>
                    <SelectItem value="hospital">Hospital User</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={globalUserFilters.user_status} onValueChange={(value) => setGlobalUserFilters({ ...globalUserFilters, user_status: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {(globalUserFilterOptions.statuses || ['Active', 'Inactive', 'Archived']).map(statusValue => (
                      <SelectItem key={statusValue} value={statusValue}>{statusValue}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="border rounded-lg overflow-auto flex-1">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User Name</TableHead>
                      <TableHead>Email ID</TableHead>
                      <TableHead>User Type</TableHead>
                      <TableHead>Hospital / Institution</TableHead>
                      <TableHead>User Category</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created Date</TableHead>
                      <TableHead>Last Login</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {globalUsersLoading ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-gray-500">Loading users...</TableCell>
                      </TableRow>
                    ) : globalUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-gray-500">No users found</TableCell>
                      </TableRow>
                    ) : globalUsers.map(globalUser => (
                      <TableRow key={globalUser.id}>
                        <TableCell className="font-medium">{globalUser.user_name || '-'}</TableCell>
                        <TableCell>{globalUser.email || '-'}</TableCell>
                        <TableCell>{globalUser.user_type || '-'}</TableCell>
                        <TableCell>{globalUser.hospital_name || '-'}</TableCell>
                        <TableCell>{globalUser.user_category || '-'}</TableCell>
                        <TableCell>
                          <Badge
                            className={
                              globalUser.status === 'Active'
                                ? 'bg-green-100 text-green-800'
                                : globalUser.status === 'Archived'
                                  ? 'bg-slate-100 text-slate-800'
                                  : 'bg-red-100 text-red-800'
                            }
                          >
                            {globalUser.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatAddedOn(globalUser.created_at)}</TableCell>
                        <TableCell>{globalUser.last_login ? formatAddedOn(globalUser.last_login) : '-'}</TableCell>
                        <TableCell>
                          <div className="flex items-center justify-center gap-2">
                            <Button variant="ghost" size="icon" onClick={() => handleViewGlobalUser(globalUser)} title="View Details">
                              <Eye className="w-4 h-4" />
                            </Button>
                            {globalUser.is_delete_allowed && (
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteGlobalUser(globalUser)} title="Delete User">
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={globalUserDetailsOpen} onOpenChange={setGlobalUserDetailsOpen}>
          <DialogContent className="sm:max-w-[620px]">
            <DialogHeader>
              <DialogTitle>User Details</DialogTitle>
              <DialogDescription>{selectedGlobalUser?.email}</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              {[
                ['User Name', selectedGlobalUser?.user_name],
                ['Email ID', selectedGlobalUser?.email],
                ['User Type', selectedGlobalUser?.user_type],
                ['Hospital / Institution', selectedGlobalUser?.hospital_name],
                ['User Category', selectedGlobalUser?.user_category],
                ['Status', selectedGlobalUser?.status],
                ['Department', selectedGlobalUser?.department_name],
                ['Job Role', selectedGlobalUser?.job_function_name],
                ['Employee ID', selectedGlobalUser?.employee_id],
                ['Created Date', formatAddedOn(selectedGlobalUser?.created_at)],
                ['Last Login', selectedGlobalUser?.last_login ? formatAddedOn(selectedGlobalUser.last_login) : '-'],
                ['Active Sessions', selectedGlobalUser?.details?.active_sessions ?? '-'],
              ].map(([label, value]) => (
                <div key={label} className="border rounded-md p-3">
                  <p className="text-xs text-gray-500">{label}</p>
                  <p className="font-medium text-gray-900 break-words">{value || '-'}</p>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setGlobalUserDetailsOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={globalUserDeleteOpen} onOpenChange={setGlobalUserDeleteOpen}>
          <DialogContent className="sm:max-w-[560px]">
            <DialogHeader>
              <DialogTitle>Delete User</DialogTitle>
              <DialogDescription>
                Are you sure you want to permanently delete this user? This action will permanently delete the user account and all associated records. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm font-semibold text-red-900">{selectedGlobalUser?.user_name}</p>
              <p className="text-xs text-red-700">{selectedGlobalUser?.email}</p>
              <p className="text-xs text-red-700 mt-1">{selectedGlobalUser?.user_type} · {selectedGlobalUser?.hospital_name || 'No institution'}</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setGlobalUserDeleteOpen(false)} disabled={globalUserDeleting}>Cancel</Button>
              <Button className="bg-red-600 hover:bg-red-700" onClick={confirmDeleteGlobalUser} disabled={globalUserDeleting}>
                {globalUserDeleting ? 'Deleting...' : 'Delete User'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Deactivate Confirmation Modal */}
        <Dialog open={deactivateModalOpen} onOpenChange={setDeactivateModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {selectedHospital?.is_active ? 'Deactivate Hospital' : 'Activate Hospital'}
              </DialogTitle>
              <DialogDescription>
                {selectedHospital?.is_active 
                  ? 'Are you sure you want to deactivate this hospital? They will lose access to the platform.'
                  : 'Are you sure you want to activate this hospital? They will regain access to the platform.'}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeactivateModalOpen(false)}>Cancel</Button>
              <Button 
                className={selectedHospital?.is_active ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}
                onClick={confirmDeactivate}
              >
                {selectedHospital?.is_active ? 'Deactivate' : 'Activate'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Delete Institution</DialogTitle>
              <DialogDescription>
                This institution has no activity and can be safely deleted. Do you also want to delete the related lead record?
              </DialogDescription>
            </DialogHeader>
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg my-2">
              <p className="text-sm text-red-800 font-medium">{selectedHospital?.hospital_name}</p>
              <p className="text-xs text-red-600 mt-1">{selectedHospital?.email}</p>
            </div>
            <DialogFooter className="flex flex-wrap gap-3 sm:justify-end">
              <Button 
                variant="outline" 
                onClick={() => setDeleteModalOpen(false)}
                className="flex-1 sm:flex-none"
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={() => confirmDelete(false)}
                disabled={deleting}
                className="flex-1 sm:flex-none"
              >
                {deleting ? 'Deleting...' : 'Delete Institution Only'}
              </Button>
              <Button 
                className="bg-red-600 hover:bg-red-700 flex-1 sm:flex-none"
                onClick={() => confirmDelete(true)}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Institution & Lead'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default HospitalsPage;
